/**
 * Hall-plan SVG import (Illustrator export following the guideline "Hallenplan-Import fuer die
 * Standbuchung", 12.10.2026).
 *
 * Faithful port of scripts/booth-map/fixtures/hallplan-parser.reference.js (parseHallPlan,
 * matchWithSystem, DEFAULTS: identical geometry, rounding, issue codes and messages) plus:
 *  - sanitization of the returned SVG texts (option sanitize, default true): parseHallPlan returns
 *    a sanitized backgroundSvg (original without the stands layer) and a sanitized sourceSvg
 *    (whole original). Uploaded SVGs are never inlined into the DOM; they are rendered as <img>.
 *  - helpers for the admin import (standsToAreaInputs incl. placeholder areas for unnamed shapes,
 *    UNNAMED_AREA_PREFIX / isUnnamedBoothNumber, activation rule activationBlockers /
 *    canActivatePlan / unnamedCount, sortIssues, issueCounts, viewBox strings, planFromAreas,
 *    systemStandsFromMap).
 *
 * Kept free of React, i18next and Amplify so it can be tested with node:test,
 * see scripts/booth-map/hallplanParser.test.js. In the browser the native DOMParser /
 * XMLSerializer are used; in Node pass them via options.DOMParser / options.XMLSerializer
 * (e.g. from jsdom). Issue messages of the reference codes are German plain text, as in the
 * reference parser; the variant codes carry English messages (see ISSUE_MESSAGE_KEYS).
 *
 *   import { parseHallPlan, matchWithSystem } from 'utils/hallplanParser';
 *   const plan = parseHallPlan(svgText);            // Geometrie + Pruefbericht
 *   const link = matchWithSystem(plan, standRows);  // Abgleich mit den Stammdaten
 */
import {
  BOOTH_NUMBER_MAX_LENGTH,
  checkBoothNumber,
  compareBoothNumbers,
  featureKeyOf,
  isValidVariantCode,
  normalizeBoothNumber,
  normalizeVariant,
  parsePoints,
  pointsToString,
  positionTypeForOpenSides,
  ruleOffersFor,
} from './boothMap.js';

// Booth features (data-position / data-open-sides / data-zone, docs/plan-standangebot-modell.md 2.2):
// stands get positionType / zone / openSides / attributes and the summary positionTypes / zones ONLY when
// the export carries them (old exports keep their exact results). positionType = data-position (free key,
// trimmed), else derived from data-open-sides (1 row, 2 corner, 3 head, 4 island); zone = data-zone of the
// stand or of the NEAREST ancestor group; attributes = the other data-* attributes of the stand (prefix
// "data-" dropped). Booth numbers are checked leniently (checkBoothNumber): reserved-id (error), id-whitespace
// and id-too-long (warnings) replace the fixed pattern of the reference parser (invalid-id).

// Booth variants (data-variant / data-size on the stand elements, see docs/hallplan-export-guideline.md):
// stands get `variant` / `size` and the plan `unitsPerMeter` / `summary.variants` ONLY when the export
// carries them, so the results for exports without these attributes stay identical to the reference
// parser (parity tests in scripts/booth-map/hallplanParser.test.js). Additional warnings:
// missing-variant, invalid-variant, invalid-size, variant-size-mismatch, size-geometry-mismatch
// (parseHallPlan) and variant-without-product, booked-stand-variant-changed (matchWithSystem).
// These codes do not exist in the reference parser: their `message` is English (the i18n key filled
// with `params`), and the UI renders them translated via issueMessageKey / ISSUE_MESSAGE_KEYS.

/**
 * English i18n keys ({{var}} placeholders, filled from issue.ref and issue.params) of the issue codes
 * added for booth variants. Codes of the reference parser keep their German plain-text message.
 */
export const ISSUE_MESSAGE_KEYS = {
  'missing-variant': 'Booth without variant: data-variant is missing, the booth is not assigned to any variant product.',
  'invalid-variant': '"{{ref}}" is not a valid variant code (letters, digits, dot, underscore, hyphen; no spaces).',
  'invalid-size': 'data-size "{{value}}" is not a positive number and is ignored.',
  'variant-size-mismatch': 'Variant "{{ref}}" has different data-size values: {{sizes}} m².',
  'size-geometry-mismatch': 'Plan approx. {{estimate}} m², data-size {{size}} m².',
  'variant-without-product': 'Plan code "{{ref}}": {{count}} booths in the plan, but no offer with this code or alias.',
  'booked-stand-variant-changed': 'Booked booth has a different variant than in the previous version.',
  'missing-position': 'Booth without position type: data-position is missing.',
  'invalid-open-sides': 'data-open-sides "{{value}}" is not a number from 1 to 4 and is ignored.',
  'reserved-id': '"{{ref}}" starts with "#", which is reserved for shapes without a name.',
  'id-whitespace': 'Booth number "{{ref}}" contains spaces; they are collapsed for matching.',
  'id-too-long': 'Booth number "{{ref}}" is longer than {{max}} characters.',
};

/** Interpolation values of an issue: { ref, ...params } (strings / numbers only). */
function issueParams(issue) {
  return { ref: issue?.ref ?? '', ...((issue && issue.params) || {}) };
}

/** Fills the {{var}} placeholders of a key (stored English fallback message). */
function fillKey(key, params) {
  return key.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (params[name] === undefined || params[name] === null ? match : String(params[name])));
}

/** A variant issue with English message + params (code must be a key of ISSUE_MESSAGE_KEYS). */
function variantIssue(code, fields, params = {}, level = 'warning') {
  const issue = { level, code, ...fields };
  if (Object.keys(params).length > 0) issue.params = params;
  issue.message = fillKey(ISSUE_MESSAGE_KEYS[code], issueParams(issue));
  return issue;
}

/**
 * { key, params } for rendering an issue with t(key, params), or null for codes without a key
 * (reference-parser codes and legacy stored issues: render issue.message as is).
 */
export function issueMessageKey(issue) {
  const key = issue && ISSUE_MESSAGE_KEYS[issue.code];
  return key ? { key, params: issueParams(issue) } : null;
}

export const DEFAULTS = {
  standsLayer: 'staende',        // Ebene mit den Standflächen (Illustrator-Ebenenname = id der <g>)
  backgroundLayer: 'hintergrund',
  idPattern: /^\s*[^#\s]/,       // Standnummer: nicht leer, kein führendes '#' (jedes Nummernschema, z. B. D3.5, A-12, 1.234, H3/B12, Z3)
  precision: 2,                  // Rundung der Koordinaten in viewBox-Einheiten
  overlapRatio: 0.1,             // ab diesem Anteil der kleineren Fläche gilt Überlappen als Problem
  labelSizeMax: 6,               // Schriftgröße der Standnummer in viewBox-Einheiten
  sanitize: true,                // backgroundSvg / sourceSvg bereinigen (script, on*, externe hrefs ...)
  sizeTolerance: 0.15,           // erlaubte Abweichung Geometrie <-> data-size (size-geometry-mismatch)
};

const SHAPES = new Set(['rect', 'polygon', 'polyline', 'path']);
const UNSUPPORTED = new Set(['circle', 'ellipse', 'line', 'use', 'text', 'image']);
const SKIP_SUBTREES = new Set(['defs', 'clipPath', 'mask', 'symbol', 'marker']);

export function parseHallPlan(svgText, options = {}) {
  const o = { ...DEFAULTS, ...options };
  const Parser = o.DOMParser || (typeof DOMParser !== 'undefined' ? DOMParser : undefined);
  const Serializer = o.XMLSerializer || (typeof XMLSerializer !== 'undefined' ? XMLSerializer : undefined);
  const issues = [];
  const fail = (code, message) => { issues.push({ level: 'error', code, message }); return { ok: false, issues, stands: [], summary: {} }; };

  const sanitize = o.sanitize !== false;
  const doc = new Parser().parseFromString(svgText, 'image/svg+xml');
  const svg = doc.documentElement;
  // parsererror first: Chrome wraps a broken file in a <parsererror> document, so the not-svg check would hide the real cause
  const perr = doc.getElementsByTagName('parsererror')[0];
  if (perr) return fail('xml-invalid', 'SVG ist kein gültiges XML: ' + (perr.textContent || '').slice(0, 200));
  if (!svg || svg.localName !== 'svg') return fail('not-svg', 'Datei ist kein SVG.');

  let viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  if (viewBox.length !== 4 || viewBox.some(Number.isNaN)) {
    const w = parseFloat(svg.getAttribute('width')), h = parseFloat(svg.getAttribute('height'));
    if (!(w > 0 && h > 0)) return fail('no-viewbox', 'SVG hat weder viewBox noch width/height.');
    viewBox = [0, 0, w, h];
  }
  if (!(viewBox[2] > 0 && viewBox[3] > 0) || viewBox.some(v => !Number.isFinite(v))) {
    return fail('no-viewbox', 'viewBox hat keine gültige Größe.');
  }

  const layer = doc.getElementById(o.standsLayer);
  if (!layer) return fail('no-stands-layer', `Ebene "${o.standsLayer}" fehlt (Illustrator-Ebene umbenennen).`);
  if (!doc.getElementById(o.backgroundLayer)) {
    issues.push({ level: 'warning', code: 'no-background-layer', message: `Ebene "${o.backgroundLayer}" fehlt, der Hintergrund bleibt leer.` });
  }

  // 1. Standflächen einsammeln (Gruppen werden durchlaufen, defs/clipPath ignoriert)
  const stands = [];
  let n = 0;
  for (const el of leafShapes(layer, issues)) {
    n += 1;
    const rawId = el.getAttribute('id');
    // a whitespace-only name counts as no name (missing-id)
    const id = rawId && !rawId.trim() ? null : rawId;
    const ref = id || `(unbenannt #${n})`;
    const local = localPoints(el, issues, ref);
    if (!local) continue;

    // 2. Transformationen vom Element bis zur Wurzel auflösen
    const M = totalTransform(el, issues, ref);
    const pts = local.map(([x, y]) => [
      round(M[0] * x + M[2] * y + M[4], o.precision),
      round(M[1] * x + M[3] * y + M[5], o.precision),
    ]);
    if (pts.some(pt => !Number.isFinite(pt[0]) || !Number.isFinite(pt[1]))) {
      issues.push({ level: 'error', code: 'degenerate', ref, message: 'Koordinaten sind ungültig (NaN).' });
      continue;
    }

    // 3. Geometrie
    const area = Math.abs(shoelace(pts));
    if (area < 1e-6) { issues.push({ level: 'error', code: 'degenerate', ref, message: 'Form hat keine Fläche.' }); continue; }
    const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
    const bbox = { x: Math.min(...xs), y: Math.min(...ys), w: round(Math.max(...xs) - Math.min(...xs), o.precision), h: round(Math.max(...ys) - Math.min(...ys), o.precision) };
    const c = centroid(pts, area);
    const stand = {
      id,
      element: el.localName,
      points: pts,
      bbox,
      centroid: [round(c[0], o.precision), round(c[1], o.precision)],
      areaUnits: round(area, o.precision),
      labelSize: round(Math.min(o.labelSizeMax, 0.35 * Math.min(bbox.w, bbox.h)), 2),
    };
    // Variante (Synonym data-offer) und Größe aus dem Export (Schlüssel nur, wenn vorhanden)
    const variantAttr = (el.getAttribute('data-variant') || '').trim() || (el.getAttribute('data-offer') || '').trim();
    if (variantAttr) stand.variant = variantAttr;
    const sizeRaw = (el.getAttribute('data-size') || '').trim();
    const sizeAttr = Number(sizeRaw.replace(',', '.'));
    if (Number.isFinite(sizeAttr) && sizeAttr > 0) stand.size = sizeAttr;
    else if (sizeRaw) issues.push(variantIssue('invalid-size', { ref, at: stand.centroid }, { value: sizeRaw }));
    // Merkmale: Positionstyp, offene Seiten, Zone (auch von einer Elterngruppe), weitere data-*-Attribute
    readFeatures(el, stand, issues, ref);
    stands.push(stand);

    // 4. Standnummer prüfen (locker: nicht leer, kein führendes '#'; Hinweise bei Leerzeichen / Überlänge)
    const check = checkBoothNumber(id);
    if (!id) issues.push({ level: 'error', code: 'missing-id', ref, at: stand.centroid, message: 'Stand ohne Namen: Objektname in Illustrator fehlt.' });
    else if (check.error === 'reserved-id') issues.push(variantIssue('reserved-id', { ref, at: stand.centroid }, {}, 'error'));
    else if (o.idPattern !== DEFAULTS.idPattern && !o.idPattern.test(id)) issues.push({ level: 'error', code: 'invalid-id', ref, at: stand.centroid, message: `"${id}" entspricht nicht dem Muster einer Standnummer.` });
    else {
      if (check.warnings.includes('id-whitespace')) issues.push(variantIssue('id-whitespace', { ref, at: stand.centroid }));
      if (check.warnings.includes('id-too-long')) issues.push(variantIssue('id-too-long', { ref, at: stand.centroid }, { max: BOOTH_NUMBER_MAX_LENGTH }));
    }
  }

  // 5. Doppelte Nummern
  const byId = new Map();
  for (const s of stands) if (s.id) { if (!byId.has(s.id)) byId.set(s.id, []); byId.get(s.id).push(s); }
  for (const [id, list] of byId) {
    if (list.length > 1) issues.push({ level: 'error', code: 'duplicate-id', ref: id, at: list[0].centroid, message: `Standnummer "${id}" kommt ${list.length}-mal vor.` });
  }

  // 6. Deckungsgleiche Formen (doppelt exportiert) und Überlappungen
  const geoKey = s => s.points.map(p => p.join(',')).sort().join(';');
  const keyOf = new Map(stands.map(s => [s, geoKey(s)]));
  const seen = new Map();
  for (const s of stands) {
    const k = keyOf.get(s);
    if (seen.has(k)) issues.push({ level: 'warning', code: 'duplicate-geometry', ref: s.id || '(unbenannt)', at: s.centroid, message: `Deckungsgleich mit ${seen.get(k).id || '(unbenannt)'}: wahrscheinlich doppelt exportiert.` });
    else seen.set(k, s);
  }
  for (let i = 0; i < stands.length; i++) {
    for (let j = i + 1; j < stands.length; j++) {
      const a = stands[i], b = stands[j];
      if (keyOf.get(a) === keyOf.get(b)) continue;
      const ix = Math.min(a.bbox.x + a.bbox.w, b.bbox.x + b.bbox.w) - Math.max(a.bbox.x, b.bbox.x);
      const iy = Math.min(a.bbox.y + a.bbox.h, b.bbox.y + b.bbox.h) - Math.max(a.bbox.y, b.bbox.y);
      if (ix <= 0 || iy <= 0) continue;
      const r = (ix * iy) / Math.min(a.bbox.w * a.bbox.h, b.bbox.w * b.bbox.h);
      if (r > o.overlapRatio) issues.push({ level: 'warning', code: 'overlap', ref: `${a.id || '?'} / ${b.id || '?'}`, at: a.centroid, message: `Überlappen sich zu ${Math.round(r * 100)} % (Bounding-Box).` });
    }
  }

  // 6b. Standvarianten (data-variant) und Größen (data-size)
  const unitsPerMeter = unitsPerMeterFromSizes(stands);
  checkVariants(stands, issues, unitsPerMeter, o.sizeTolerance);
  const variantCodes = new Set(stands.map(s => normalizeVariant(s.variant)).filter(Boolean));
  // 6c. Merkmale: alle oder keiner (missing-position), Zähler je Positionstyp / Zone
  if (stands.some(s => s.positionType !== undefined)) {
    for (const s of stands) {
      if (s.id && s.positionType === undefined) issues.push(variantIssue('missing-position', { ref: s.id, at: s.centroid }));
    }
  }
  const positionCounts = featureCounts(stands, 'positionType');
  const zoneCounts = featureCounts(stands, 'zone');

  // 7. Hintergrund = Original ohne die Standebene (bereinigt); sourceSvg = bereinigtes Original
  const clone = doc.cloneNode(true);
  if (sanitize) sanitizeSvgDocument(clone);
  const l2 = clone.getElementById(o.standsLayer);
  if (l2 && l2.parentNode) l2.parentNode.removeChild(l2);
  const backgroundSvg = new Serializer().serializeToString(clone);
  const source = doc.cloneNode(true);
  if (sanitize) sanitizeSvgDocument(source);
  const sourceSvg = new Serializer().serializeToString(source);

  const errors = issues.filter(i => i.level === 'error').length;
  const summary = { shapes: n, stands: stands.length, withId: stands.filter(s => s.id).length, errors, warnings: issues.length - errors };
  if (variantCodes.size > 0) summary.variants = variantCodes.size;
  if (positionCounts) summary.positionTypes = positionCounts;
  if (zoneCounts) summary.zones = zoneCounts;
  return {
    ok: errors === 0,
    viewBox,
    stands,
    issues,
    backgroundSvg,
    sourceSvg,
    summary,
    ...(unitsPerMeter ? { unitsPerMeter } : {}),
  };
}

/**
 * Scale of the plan from the data-size of its stands: median of sqrt(areaUnits / size) over the stands
 * with a size (viewBox units per meter); null when no stand has a size.
 */
export function unitsPerMeterFromSizes(stands) {
  const ratios = (stands || [])
    .filter(s => s && s.size > 0 && s.areaUnits > 0)
    .map(s => Math.sqrt(s.areaUnits / s.size))
    .sort((a, b) => a - b);
  return ratios.length ? ratios[Math.floor(ratios.length / 2)] : null;
}

/** data-* attributes with a meaning of their own (not copied to stand.attributes); data-name is Illustrator's layer name. */
const KNOWN_DATA_ATTRIBUTES = new Set(['data-variant', 'data-offer', 'data-size', 'data-position', 'data-open-sides', 'data-zone', 'data-name']);

/**
 * Features of a stand element (keys only when present): positionType (data-position, trimmed, free key;
 * without it derived from data-open-sides), openSides (data-open-sides, integer 1-4; anything else ->
 * warning invalid-open-sides), zone (data-zone of the element or of the nearest ancestor with a non-empty
 * value), attributes ({ name: trimmed value } of the other non-empty data-* attributes, prefix dropped).
 */
function readFeatures(el, stand, issues, ref) {
  const position = (el.getAttribute('data-position') || '').trim();
  const openRaw = (el.getAttribute('data-open-sides') || '').trim();
  let openSides = null;
  if (openRaw) {
    const value = Number(openRaw);
    if (positionTypeForOpenSides(value)) openSides = value;
    else issues.push(variantIssue('invalid-open-sides', { ref, at: stand.centroid }, { value: openRaw }));
  }
  if (position) stand.positionType = position;
  else if (openSides !== null) stand.positionType = positionTypeForOpenSides(openSides);
  if (openSides !== null) stand.openSides = openSides;
  for (let e = el; e && e.nodeType === 1; e = e.parentNode) {
    const zone = (e.getAttribute('data-zone') || '').trim();
    if (zone) {
      stand.zone = zone;
      break;
    }
  }
  const attributes = {};
  for (const attr of [...el.attributes]) {
    const name = String(attr.name || '').toLowerCase();
    if (!name.startsWith('data-') || KNOWN_DATA_ATTRIBUTES.has(name)) continue;
    const value = String(attr.value || '').trim();
    if (value && name.length > 5) attributes[name.slice(5)] = value;
  }
  if (Object.keys(attributes).length > 0) stand.attributes = attributes;
}

/**
 * { key: count } of the stands carrying a feature (positionType / zone), keys grouped case-insensitively
 * (spelling of the first stand), in order of first appearance; null when no stand carries it.
 */
function featureCounts(stands, field) {
  const counts = {};
  const spelling = new Map();
  for (const s of stands) {
    if (s[field] === undefined) continue;
    const lower = s[field].toLowerCase();
    if (!spelling.has(lower)) spelling.set(lower, s[field]);
    const key = spelling.get(lower);
    counts[key] = (counts[key] || 0) + 1;
  }
  return spelling.size > 0 ? counts : null;
}

// Warnungen zu data-variant / data-size (nie Fehler: die Aktivierungsregel bleibt unverändert)
function checkVariants(stands, issues, unitsPerMeter, tolerance) {
  const withVariant = stands.filter(s => s.variant !== undefined);
  if (withVariant.length > 0) {
    for (const s of stands) {
      if (s.id && s.variant === undefined) issues.push(variantIssue('missing-variant', { ref: s.id, at: s.centroid }));
    }
    const invalid = new Set();
    for (const s of withVariant) {
      if (isValidVariantCode(s.variant) || invalid.has(s.variant)) continue;
      invalid.add(s.variant);
      issues.push(variantIssue('invalid-variant', { ref: s.variant, at: s.centroid }));
    }
    const sizesByCode = new Map();
    for (const s of withVariant) {
      if (s.size === undefined) continue;
      const code = normalizeVariant(s.variant);
      if (!sizesByCode.has(code)) sizesByCode.set(code, new Set());
      sizesByCode.get(code).add(s.size);
    }
    for (const [code, sizes] of sizesByCode) {
      if (sizes.size > 1) issues.push(variantIssue('variant-size-mismatch', { ref: code }, { sizes: [...sizes].sort((a, b) => a - b).join(', ') }));
    }
  }
  if (!unitsPerMeter) return;
  for (const s of stands) {
    if (!(s.size > 0)) continue;
    const est = s.areaUnits / (unitsPerMeter * unitsPerMeter);
    if (Math.abs(est - s.size) / s.size > tolerance) {
      issues.push(variantIssue('size-geometry-mismatch', { ref: s.id || '(unbenannt)', at: s.centroid }, { estimate: est.toFixed(1), size: s.size }));
    }
  }
}

/** Most frequent value of a list of numbers (ties: the smaller one); null for an empty list. */
function mostCommon(values) {
  const counts = new Map();
  for (const v of values) counts.set(v, (counts.get(v) || 0) + 1);
  let best = null;
  let bestCount = 0;
  for (const [v, c] of counts) if (c > bestCount || (c === bestCount && v < best)) { best = v; bestCount = c; }
  return best;
}

// Abgleich mit den Stammdaten des Buchungssystems.
// systemStands (see systemStandsFromMap): physical booths [{ standNo, sizeM2?, hasBookings?, variant? }] and
// variant products [{ variant, aliases?, productId?, eventProductId? }] (entries without standNo).
// previous: Ergebnis von parseHallPlan der Vorversion oder planFromAreas (optional).
//
// A named plan stand is matched by its number (physical booth) or, when it has a variant, by a variant
// product whose code or alias equals it (normalized). Result keys of the reference parser plus:
//  - matchedVariants: [{ code, count, size, eventProductId, productId, boothNumbers }] plan codes with a
//    variant product (stands matched by number to a booth WITHOUT variant, i.e. a single-booth product,
//    are not counted), by code;
//  - variantsWithoutProduct: [{ code, count, size, boothNumbers }] with ONE warning variant-without-product
//    per code; their stands are in onlyInPlan but get no per-stand not-in-system warning;
//  - matchedRules: [{ key (featureKeyOf), size, positionType, zone, count, eventProductId, productId, boothNumbers }]
//    stands without number match and without code product that match an offer's match rule (system entries
//    with matchRule, see systemStandsFromMap; ranking as matchAreasToProducts stage 3), per offer and feature
//    combination; such stands count as matched and are not reported as variant-without-product;
//  - diff.variantChanged: stands of both versions whose variant differs (only when the previous version
//    carries variants at all - legacy areas without variant are not compared) and the warning
//    booked-stand-variant-changed for booked ones.
// unitsPerMeter: from the number matches with a size (as in the reference), else from data-size.
export function matchWithSystem(plan, systemStands, previous = null, tolerance = 0.15) {
  const planIds = new Map(plan.stands.filter(s => s.id).map(s => [s.id, s]));
  const system = (systemStands || []).filter(Boolean);
  const sysIds = new Map(system.filter(s => s.standNo).map(s => [s.standNo, s]));
  const variantEntries = new Map();
  const ruleDescriptors = [];
  for (const s of system) {
    if (s.standNo) continue;
    for (const code of [s.variant, ...(Array.isArray(s.aliases) ? s.aliases : [])].map(normalizeVariant)) {
      if (code && !variantEntries.has(code)) variantEntries.set(code, s);
    }
    if (s.matchRule !== undefined && s.matchRule !== null) {
      ruleDescriptors.push({ eventProductId: s.eventProductId ?? null, productId: s.productId ?? null, boothMatchRule: s.matchRule, sortKey: s.sortKey, entry: s });
    }
  }

  const matched = [];
  const onlyInPlan = [];
  const withProduct = new Map();
  const withoutProduct = new Map();
  const withRule = new Map();
  for (const [id, stand] of planIds) {
    const sys = sysIds.get(id);
    const code = normalizeVariant(stand.variant);
    const entry = code ? variantEntries.get(code) : undefined;
    // rule stage: a stand without number match and without code product takes the first matching rule offer
    const ruleOffer = !sys && !entry && ruleDescriptors.length ? ruleOffersFor(stand, ruleDescriptors)[0] : undefined;
    if (code && !(sys && !normalizeVariant(sys.variant)) && !ruleOffer) {
      const groups = entry ? withProduct : withoutProduct;
      if (!groups.has(code)) groups.set(code, { code, entry, stands: [] });
      groups.get(code).stands.push(stand);
    }
    if (ruleOffer) {
      const key = `${ruleOffer.eventProductId ?? ''}#${featureKeyOf(stand).toLowerCase()}`;
      if (!withRule.has(key)) withRule.set(key, { descriptor: ruleOffer, stand, stands: [] });
      withRule.get(key).stands.push(stand);
    }
    if (sys || entry || ruleOffer) matched.push(id);
    else onlyInPlan.push(id);
  }
  // stands matched by rule, per offer and feature combination
  const matchedRules = [...withRule.values()].map((g) => ({
    key: featureKeyOf(g.stand),
    size: g.stand.size > 0 ? g.stand.size : null,
    positionType: g.stand.positionType ?? null,
    zone: g.stand.zone ?? null,
    count: g.stands.length,
    eventProductId: g.descriptor.eventProductId,
    productId: g.descriptor.productId,
    boothNumbers: g.stands.map(s => s.id).sort(compareBoothNumbers),
  }));
  const groupList = (groups) => [...groups.values()]
    .sort((a, b) => compareBoothNumbers(a.code, b.code))
    .map(g => ({
      code: g.code,
      count: g.stands.length,
      size: mostCommon(g.stands.map(s => s.size).filter(v => v > 0)),
      ...(g.entry ? { eventProductId: g.entry.eventProductId ?? null, productId: g.entry.productId ?? null } : {}),
      boothNumbers: g.stands.map(s => s.id).sort(compareBoothNumbers),
    }));
  const matchedVariants = groupList(withProduct);
  const variantsWithoutProduct = groupList(withoutProduct);

  const onlyInSystem = [...sysIds.keys()].filter(id => !planIds.has(id));
  const issues = [];
  for (const id of onlyInPlan) {
    if (!normalizeVariant(planIds.get(id).variant)) issues.push({ level: 'warning', code: 'not-in-system', ref: id, message: 'Stand im Plan, aber nicht in den Stammdaten.' });
  }
  for (const g of variantsWithoutProduct) {
    issues.push(variantIssue('variant-without-product', { ref: g.code }, { count: g.count }));
  }
  for (const id of onlyInSystem) {
    const s = sysIds.get(id);
    issues.push({ level: s.hasBookings ? 'error' : 'warning', code: 'not-in-plan', ref: id, message: s.hasBookings ? 'Gebuchter Stand fehlt im Plan.' : 'Stand in den Stammdaten, aber nicht im Plan.' });
  }

  // Maßstab aus den gematchten Ständen schätzen (Median), dann Flächen vergleichen; sonst aus data-size
  const ratios = matched.filter(id => sysIds.get(id)?.sizeM2 > 0).map(id => Math.sqrt(planIds.get(id).areaUnits / sysIds.get(id).sizeM2)).sort((a, b) => a - b);
  const unitsPerMeter = ratios.length ? ratios[Math.floor(ratios.length / 2)] : (plan.unitsPerMeter || unitsPerMeterFromSizes(plan.stands));
  if (unitsPerMeter) {
    for (const id of matched) {
      const size = sysIds.get(id)?.sizeM2;
      if (!(size > 0)) continue;
      const est = planIds.get(id).areaUnits / (unitsPerMeter * unitsPerMeter);
      if (Math.abs(est - size) / size > tolerance) issues.push({ level: 'warning', code: 'area-mismatch', ref: id, message: `Plan ca. ${est.toFixed(1)} m², Stammdaten ${size} m².` });
    }
  }

  // Diff zur Vorversion
  const diff = { added: [], removed: [], changed: [], variantChanged: [] };
  if (previous) {
    const prev = new Map(previous.stands.filter(s => s.id).map(s => [s.id, s]));
    const sameFrame = JSON.stringify(previous.viewBox) === JSON.stringify(plan.viewBox);
    for (const id of planIds.keys()) {
      if (!prev.has(id)) diff.added.push(id);
      else if (!sameFrame || JSON.stringify(prev.get(id).points) !== JSON.stringify(planIds.get(id).points)) diff.changed.push(id);
    }
    for (const id of prev.keys()) if (!planIds.has(id)) diff.removed.push(id);
    const prevHasVariants = [...prev.values()].some(s => normalizeVariant(s.variant));
    if (prevHasVariants) {
      for (const [id, stand] of planIds) {
        if (prev.has(id) && normalizeVariant(prev.get(id).variant) !== normalizeVariant(stand.variant)) diff.variantChanged.push(id);
      }
    }
    if (!sameFrame) issues.push({ level: 'warning', code: 'viewbox-changed', message: 'Zeichenfläche hat sich geändert, Geometrie-Diff nicht belastbar.' });
    for (const id of diff.changed) if (sysIds.get(id)?.hasBookings) issues.push({ level: 'warning', code: 'booked-stand-moved', ref: id, message: 'Gebuchter Stand hat eine andere Form oder Lage als in der Vorversion.' });
    for (const id of diff.variantChanged) if (sysIds.get(id)?.hasBookings) issues.push(variantIssue('booked-stand-variant-changed', { ref: id }));
  }
  return { matched, onlyInPlan, onlyInSystem, unitsPerMeter, diff, issues, matchedVariants, variantsWithoutProduct, matchedRules };
}

// ---- Sanitization ------------------------------------------------------------

// Elements that can run code, embed foreign content or animate attributes (e.g. an animated href).
const DANGEROUS_ELEMENTS = new Set([
  'script', 'foreignobject', 'iframe', 'object', 'embed',
  'animate', 'animatemotion', 'animatetransform', 'animatecolor', 'set',
]);

/**
 * Sanitizes a parsed SVG document in place: removes script, foreignObject, iframe, object, embed
 * and animate* / set elements, every attribute whose name starts with "on", href / xlink:href
 * values that are neither "#fragment" references nor embedded raster images (data:image/...),
 * <style> blocks and style attributes with @import or external url(...), processing instructions and the doctype.
 * Returns the document.
 */
// url(...) that is not a #fragment, or an @import: only allowed inline references stay
const EXTERNAL_CSS_RE = /@import|url\(\s*['"]?\s*(?!#)/i;
// "#fragment" references and embedded raster images (Illustrator "Bilder einbetten") are kept
const isSafeHref = value => value.startsWith('#') || /^data:image\/(png|jpe?g|gif|webp);/i.test(value);

export function sanitizeSvgDocument(doc) {
  if (!doc) return doc;
  for (const node of [...doc.childNodes]) {
    // 7 = PROCESSING_INSTRUCTION_NODE (xml-stylesheet ...), 10 = DOCUMENT_TYPE_NODE (entities)
    if (node.nodeType === 7 || node.nodeType === 10) doc.removeChild(node);
  }
  const root = doc.documentElement;
  if (!root) return doc;
  const stack = [root];
  while (stack.length) {
    const el = stack.pop();
    for (const child of [...el.childNodes]) {
      if (child.nodeType === 7) { el.removeChild(child); continue; }
      if (child.nodeType !== 1) continue;
      if (DANGEROUS_ELEMENTS.has(String(child.localName || '').toLowerCase())) { el.removeChild(child); continue; }
      stack.push(child);
    }
    if (String(el.localName || '').toLowerCase() === 'style' && EXTERNAL_CSS_RE.test(el.textContent || '')) {
      // <style> with @import or url(http...) could pull external resources: drop the whole block
      if (el.parentNode) el.parentNode.removeChild(el);
      continue;
    }
    for (const attr of [...el.attributes]) {
      const name = String(attr.localName || attr.name || '').toLowerCase();
      const value = String(attr.value || '').trim();
      if (name.startsWith('on')) { el.removeAttributeNode(attr); continue; }
      if (name === 'href' && !isSafeHref(value)) { el.removeAttributeNode(attr); continue; }
      if (name === 'style' && EXTERNAL_CSS_RE.test(value)) el.removeAttributeNode(attr);
    }
  }
  return doc;
}

/**
 * Parses, sanitizes (see sanitizeSvgDocument) and serializes an SVG text.
 * Options: { DOMParser, XMLSerializer } (default: the browser globals). Returns '' when the text is not a
 * well-formed SVG document.
 */
export function sanitizeSvgText(svgText, options = {}) {
  const Parser = options.DOMParser || (typeof DOMParser !== 'undefined' ? DOMParser : undefined);
  const Serializer = options.XMLSerializer || (typeof XMLSerializer !== 'undefined' ? XMLSerializer : undefined);
  let doc;
  try {
    doc = new Parser().parseFromString(String(svgText ?? ''), 'image/svg+xml');
  } catch (error) {
    return '';
  }
  const svg = doc && doc.documentElement;
  if (!svg || svg.localName !== 'svg' || doc.getElementsByTagName('parsererror')[0]) return '';
  sanitizeSvgDocument(doc);
  return new Serializer().serializeToString(doc);
}

// ---- Admin helpers -----------------------------------------------------------

const round2 = (v) => {
  const r = Math.round(Number(v) * 100) / 100;
  return Object.is(r, -0) ? 0 : r;
};

/**
 * Prefix of the placeholder booth number stored for a stand shape WITHOUT a name in the export
 * ('#' + 1-based index of the stand in plan.stands, e.g. '#17'). Such areas are drawn on the hall
 * plan but never matched to a booth product (not bookable). '#' can never be part of a real
 * booth number (DEFAULTS.idPattern), so placeholders cannot collide with named stands.
 */
export const UNNAMED_AREA_PREFIX = '#';

/** true when value is a placeholder booth number of an unnamed shape (starts with '#', after trimming). */
export function isUnnamedBoothNumber(value) {
  return typeof value === 'string' && value.trim().startsWith(UNNAMED_AREA_PREFIX);
}

/**
 * CreateBoothPlanAreaInput-shaped objects for the stands of a parsed plan, in plan order:
 * { eventId, planVersionId, boothNumber, points 'x,y x,y' (2 decimals), labelX, labelY (centroid),
 * labelSize, areaUnits, bbox 'x y w h', source: 'svg' } plus variant, size, positionType, zone, openSides and
 * attributes (JSON string, AWSJSON) only when the stand carries them (old exports send no new keys).
 *  - stands WITH an id: boothNumber = id (first occurrence of a duplicated id only);
 *  - stands WITHOUT an id (issue 'missing-id'), only when includeUnnamed (default true):
 *    boothNumber = UNNAMED_AREA_PREFIX + 1-based index of the stand in plan.stands (stable for
 *    the same file, e.g. '#3'). These placeholder areas are never matched to booth products.
 */
export function standsToAreaInputs(plan, { eventId, planVersionId, includeUnnamed = true } = {}) {
  const seenIds = new Set();
  const inputs = [];
  const stands = (plan && plan.stands) || [];
  for (let index = 0; index < stands.length; index += 1) {
    const s = stands[index];
    if (!s) continue;
    let boothNumber;
    if (s.id) {
      if (seenIds.has(s.id)) continue;
      seenIds.add(s.id);
      boothNumber = s.id;
    } else {
      if (!includeUnnamed) continue;
      boothNumber = `${UNNAMED_AREA_PREFIX}${index + 1}`;
    }
    const b = s.bbox || { x: 0, y: 0, w: 0, h: 0 };
    const input = {
      eventId,
      planVersionId,
      boothNumber,
      points: pointsToString(s.points),
      labelX: Array.isArray(s.centroid) ? s.centroid[0] : null,
      labelY: Array.isArray(s.centroid) ? s.centroid[1] : null,
      labelSize: s.labelSize ?? null,
      areaUnits: s.areaUnits ?? null,
      bbox: [b.x, b.y, b.w, b.h].map(round2).join(' '),
      source: 'svg',
    };
    // only when the export carries them: an old SVG must not send the new schema fields
    if (s.variant !== undefined && s.variant !== null && s.variant !== '') input.variant = s.variant;
    if (s.size !== undefined && s.size !== null) input.size = s.size;
    if (s.positionType !== undefined && s.positionType !== null && s.positionType !== '') input.positionType = s.positionType;
    if (s.zone !== undefined && s.zone !== null && s.zone !== '') input.zone = s.zone;
    if (s.openSides !== undefined && s.openSides !== null) input.openSides = s.openSides;
    // AWSJSON input: a JSON string
    if (s.attributes && typeof s.attributes === 'object' && Object.keys(s.attributes).length > 0) input.attributes = JSON.stringify(s.attributes);
    inputs.push(input);
  }
  return inputs;
}

const LEVEL_RANK = { error: 0, warning: 1 };

/** Issues sorted: errors first, then by code, then by ref (natural order). Returns a new array. */
export function sortIssues(issues) {
  return [...(issues || [])].sort((a, b) => {
    const la = LEVEL_RANK[a?.level] ?? 2;
    const lb = LEVEL_RANK[b?.level] ?? 2;
    if (la !== lb) return la - lb;
    const ca = String(a?.code ?? '');
    const cb = String(b?.code ?? '');
    if (ca !== cb) return ca < cb ? -1 : 1;
    return compareBoothNumbers(a?.ref ?? '', b?.ref ?? '');
  });
}

/**
 * Error-level issues that block activating a hall-plan version: every error except 'missing-id'
 * (unnamed shapes become unbookable placeholder areas, see UNNAMED_AREA_PREFIX). Warnings never
 * block. Returns a new array in input order.
 */
export function activationBlockers(issues) {
  return (issues || []).filter((i) => i?.level === 'error' && i.code !== 'missing-id');
}

/** true when a version with these issues may be activated (no activationBlockers). */
export function canActivatePlan(issues) {
  return activationBlockers(issues).length === 0;
}

/** Number of 'missing-id' issues (shapes without a booth number, stored as placeholder areas). */
export function unnamedCount(issues) {
  let n = 0;
  for (const i of issues || []) if (i?.code === 'missing-id') n += 1;
  return n;
}

/** { errors, warnings } of an issue list. */
export function issueCounts(issues) {
  let errors = 0;
  let warnings = 0;
  for (const i of issues || []) {
    if (i?.level === 'error') errors += 1;
    else if (i?.level === 'warning') warnings += 1;
  }
  return { errors, warnings };
}

/** [minX, minY, w, h] -> "minX minY w h" (null unless 4 finite numbers). */
export function viewBoxToString(viewBox) {
  if (!Array.isArray(viewBox) || viewBox.length !== 4) return null;
  const nums = viewBox.map(Number);
  if (!nums.every(Number.isFinite)) return null;
  return nums.join(' ');
}

/** "minX minY w h" (spaces and/or commas) -> [minX, minY, w, h]; null unless 4 numbers with w, h > 0. */
export function parseViewBoxString(str) {
  if (typeof str !== 'string') return null;
  const tokens = str.trim().split(/[\s,]+/).filter(Boolean);
  if (tokens.length !== 4) return null;
  const nums = tokens.map((t) => (/^[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?$/.test(t) ? Number(t) : NaN));
  if (!nums.every(Number.isFinite) || !(nums[2] > 0) || !(nums[3] > 0)) return null;
  return nums;
}

/**
 * A 'previous'-shaped object for matchWithSystem from stored BoothPlanArea rows:
 * { viewBox: [minX, minY, w, h] | null, stands: [{ id: boothNumber, points: [[x, y], ...], variant?, positionType?, zone? }] }
 * (variant / positionType / zone only when the area has one, so the variant diff can tell legacy rows apart).
 * Placeholder areas of unnamed shapes (isUnnamedBoothNumber) are skipped: the geometry diff only
 * covers named stands, as in parseHallPlan results.
 */
export function planFromAreas(areas, viewBoxString) {
  return {
    viewBox: parseViewBoxString(viewBoxString),
    stands: (areas || [])
      .filter((a) => a && a.boothNumber && !isUnnamedBoothNumber(a.boothNumber))
      .map((a) => {
        const stand = { id: a.boothNumber, points: parsePoints(a.points) };
        const variant = typeof a.variant === 'string' ? a.variant.trim() : '';
        if (variant) stand.variant = variant;
        const positionType = typeof a.positionType === 'string' ? a.positionType.trim() : '';
        if (positionType) stand.positionType = positionType;
        const zone = typeof a.zone === 'string' ? a.zone.trim() : '';
        if (zone) stand.zone = zone;
        return stand;
      }),
  };
}

const sizeOrNull = (value) => {
  const n = value === null || value === undefined || value === '' ? NaN : Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * System stands for matchWithSystem from a getEventBoothMap result (or, legacy, its booths array):
 *  - one entry per map booth with a number: { standNo: normalized boothNumber, sizeM2: area (else
 *    variantSize, else the product's area) | null, hasBookings: status === 'sold', variant? (normalized,
 *    only when the booth has one), positionType?, zone? (trimmed, only when the booth has them) };
 *  - one entry per offer product with a code or a match rule (map.variants, then map.variantsWithoutAreas;
 *    once per eventProductId): { variant: normalized code | null, aliases: normalized codes, productId,
 *    eventProductId, matchRule? (raw, only when set: entry.matchRule, else the product's boothMatchRule) }
 *    (no standNo).
 * Booths without a booth number are skipped.
 */
export function systemStandsFromMap(mapOrBooths) {
  const isList = Array.isArray(mapOrBooths);
  const map = !isList && mapOrBooths && typeof mapOrBooths === 'object' ? mapOrBooths : null;
  const booths = isList ? mapOrBooths : (map && Array.isArray(map.booths) ? map.booths : []);
  const products = map && map.products && typeof map.products === 'object' ? map.products : {};
  const out = [];
  for (const b of booths) {
    const standNo = normalizeBoothNumber(b?.boothNumber);
    if (!standNo) continue;
    const size = sizeOrNull(b.area) ?? sizeOrNull(b.variantSize) ?? sizeOrNull(products[b.productId]?.area);
    const entry = { standNo, sizeM2: size, hasBookings: b.status === 'sold' };
    const variant = normalizeVariant(b.variant);
    if (variant) entry.variant = variant;
    const positionType = typeof b.positionType === 'string' ? b.positionType.trim() : '';
    if (positionType) entry.positionType = positionType;
    const zone = typeof b.zone === 'string' ? b.zone.trim() : '';
    if (zone) entry.zone = zone;
    out.push(entry);
  }
  if (map) {
    const seen = new Set();
    for (const v of [...(Array.isArray(map.variants) ? map.variants : []), ...(Array.isArray(map.variantsWithoutAreas) ? map.variantsWithoutAreas : [])]) {
      if (!v) continue;
      const product = products[v.productId] || {};
      const variant = normalizeVariant(v.variant ?? product.boothVariant);
      const matchRule = v.matchRule ?? product.boothMatchRule ?? null;
      const key = v.eventProductId ?? variant;
      if ((!variant && matchRule === null) || seen.has(key)) continue;
      seen.add(key);
      const aliases = Array.isArray(v.aliases) ? v.aliases : Array.isArray(product.boothVariantAliases) ? product.boothVariantAliases : [];
      const entry = { variant: variant || null, aliases: variant ? aliases.map(normalizeVariant).filter(Boolean) : [], productId: v.productId ?? null, eventProductId: v.eventProductId ?? null };
      if (matchRule !== null) entry.matchRule = matchRule;
      out.push(entry);
    }
  }
  return out;
}

// ---- Hilfsfunktionen -------------------------------------------------------

function* leafShapes(root, issues) {
  const stack = [...root.children].reverse();
  while (stack.length) {
    const el = stack.pop();
    const tag = el.localName;
    if (SKIP_SUBTREES.has(tag)) continue;
    if (SHAPES.has(tag)) { yield el; continue; }
    if (UNSUPPORTED.has(tag)) { issues.push({ level: 'error', code: 'unsupported-element', ref: el.getAttribute('id') || tag, message: `<${tag}> wird als Standfläche nicht unterstützt (nur rect, polygon, path mit geraden Segmenten).` }); continue; }
    if (tag === 'g' || tag === 'a') stack.push(...[...el.children].reverse());
  }
}

function localPoints(el, issues, ref) {
  const num = v => parseFloat(v || '0') || 0;
  switch (el.localName) {
    case 'rect': {
      const x = num(el.getAttribute('x')), y = num(el.getAttribute('y')), w = num(el.getAttribute('width')), h = num(el.getAttribute('height'));
      return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
    }
    case 'polygon':
    case 'polyline': {
      const v = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number).filter(n => !Number.isNaN(n));
      const pts = [];
      for (let i = 0; i + 1 < v.length; i += 2) pts.push([v[i], v[i + 1]]);
      const closedByPoints = pts.length > 1 && same(pts[0], pts[pts.length - 1]);
      if (el.localName === 'polyline' && !closedByPoints) {
        issues.push({ level: 'error', code: 'open-path', ref, message: 'Polyline ist nicht geschlossen.' });
        return null;
      }
      if (closedByPoints) pts.pop();
      return pts.length >= 3 ? pts : (issues.push({ level: 'error', code: 'degenerate', ref, message: 'Weniger als 3 Punkte.' }), null);
    }
    case 'path':
      return pathPoints(el.getAttribute('d') || '', issues, ref);
    default:
      return null;
  }
}

function pathPoints(d, issues, ref) {
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || [];
  const pts = [];
  let cmd = null, cur = [0, 0], start = null, closed = false, i = 0;
  const read = () => Number(tokens[i++]);
  while (i < tokens.length) {
    const t = tokens[i];
    if (/[a-zA-Z]/.test(t)) { cmd = t; i += 1; if (cmd === 'Z' || cmd === 'z') { closed = true; continue; } }
    if (closed && cmd && cmd !== 'Z' && cmd !== 'z') {
      issues.push({ level: 'error', code: 'multi-subpath', ref, message: 'Pfad besteht aus mehreren Teilpfaden (zusammengesetzter Pfad). Pro Stand genau eine geschlossene Form.' });
      return null;
    }
    switch (cmd) {
      case 'M': cur = [read(), read()]; cmd = 'L'; break;
      case 'm': cur = pts.length ? [cur[0] + read(), cur[1] + read()] : [read(), read()]; cmd = 'l'; break;
      case 'L': cur = [read(), read()]; break;
      case 'l': cur = [cur[0] + read(), cur[1] + read()]; break;
      case 'H': cur = [read(), cur[1]]; break;
      case 'h': cur = [cur[0] + read(), cur[1]]; break;
      case 'V': cur = [cur[0], read()]; break;
      case 'v': cur = [cur[0], cur[1] + read()]; break;
      default:
        issues.push({ level: 'error', code: 'curve', ref, message: `Pfadbefehl "${cmd}" (Kurve/Bogen) wird nicht unterstützt. Standflächen nur mit geraden Kanten zeichnen.` });
        return null;
    }
    if (start === null) start = cur;
    pts.push(cur);
  }
  if (!closed && !(pts.length > 1 && same(pts[0], pts[pts.length - 1]))) {
    issues.push({ level: 'error', code: 'open-path', ref, message: 'Pfad ist nicht geschlossen (kein Z).' });
    return null;
  }
  if (pts.length > 1 && same(pts[0], pts[pts.length - 1])) pts.pop();
  return pts.length >= 3 ? pts : (issues.push({ level: 'error', code: 'degenerate', ref, message: 'Weniger als 3 Punkte.' }), null);
}

function totalTransform(el, issues, ref) {
  const chain = [];
  for (let e = el; e && e.nodeType === 1; e = e.parentNode) chain.push(e);
  let M = [1, 0, 0, 1, 0, 0];
  for (const e of chain.reverse()) M = mul(M, parseTransform(e.getAttribute('transform') || '', issues, ref));
  return M;
}

function parseTransform(str, issues, ref) {
  let M = [1, 0, 0, 1, 0, 0];
  for (const [, name, args] of str.matchAll(/(\w+)\s*\(([^)]*)\)/g)) {
    const a = args.trim().split(/[\s,]+/).filter(Boolean).map(Number);
    let N;
    switch (name) {
      case 'translate': N = [1, 0, 0, 1, a[0] || 0, a[1] || 0]; break;
      case 'scale': N = [a[0], 0, 0, a.length > 1 ? a[1] : a[0], 0, 0]; break;
      case 'rotate': {
        const r = (a[0] || 0) * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
        N = [c, s, -s, c, 0, 0];
        if (a.length > 2) N = mul(mul([1, 0, 0, 1, a[1], a[2]], N), [1, 0, 0, 1, -a[1], -a[2]]);
        break;
      }
      case 'matrix': N = a; break;
      case 'skewX': N = [1, 0, Math.tan(a[0] * Math.PI / 180), 1, 0, 0]; break;
      case 'skewY': N = [1, Math.tan(a[0] * Math.PI / 180), 0, 1, 0, 0]; break;
      default: issues.push({ level: 'error', code: 'transform', ref, message: `Unbekannte Transformation "${name}".` }); continue;
    }
    M = mul(M, N);
  }
  return M;
}

function mul(A, B) {
  return [
    A[0] * B[0] + A[2] * B[1], A[1] * B[0] + A[3] * B[1],
    A[0] * B[2] + A[2] * B[3], A[1] * B[2] + A[3] * B[3],
    A[0] * B[4] + A[2] * B[5] + A[4], A[1] * B[4] + A[3] * B[5] + A[5],
  ];
}

function shoelace(p) {
  let s = 0;
  for (let i = 0; i < p.length; i++) { const a = p[i], b = p[(i + 1) % p.length]; s += a[0] * b[1] - b[0] * a[1]; }
  return s / 2;
}

function centroid(p, area) {
  const A = shoelace(p);
  if (Math.abs(A) < 1e-9) return [p.reduce((s, q) => s + q[0], 0) / p.length, p.reduce((s, q) => s + q[1], 0) / p.length];
  let cx = 0, cy = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length], f = a[0] * b[1] - b[0] * a[1];
    cx += (a[0] + b[0]) * f; cy += (a[1] + b[1]) * f;
  }
  return [cx / (6 * A), cy / (6 * A)];
}

const same = (a, b) => a && b && Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;
const round = (v, p) => Math.round(v * 10 ** p) / 10 ** p;
